
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import AuthHeader from './AuthHeader';
import AuthForm from './AuthForm';

const LoginPage = () => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkingGuest, setCheckingGuest] = useState(true);
  const [guestData, setGuestData] = useState<{email: string | null, name: string | null}>({
    email: null,
    name: null
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if this is from a guest checkout
        const guestEmail = localStorage.getItem('guestSignupEmail');
        const guestName = localStorage.getItem('guestSignupName');
        
        if (guestEmail) {
          setGuestData({
            email: guestEmail,
            name: guestName
          });
        }
        
        // Get fresh session
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          if (sessionError.message?.includes('refresh_token_not_found')) {
            console.log('No valid session found, user needs to login');
            setLoading(false);
            setCheckingGuest(false);
            return;
          }
          throw sessionError;
        }

        if (currentSession) {
          setSession(currentSession);
          // Regular redirect to chat since account is already created
          setCheckingGuest(false);
        } else {
          setCheckingGuest(false);
        }
      } catch (error: any) {
        console.error('Error initializing auth:', error);
        toast.error('Error al verificar el estado de autenticación. Por favor, inicie sesión nuevamente.');
        setCheckingGuest(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log('Auth state changed:', event);
      
      switch (event) {
        case 'SIGNED_IN':
          toast.success('¡Inicio de sesión exitoso!');
          setSession(currentSession);
          
          // Get return path if available
          const returnPath = (location.state as any)?.from || '/chat';
          navigate(returnPath, { replace: true });
          break;
          
        case 'SIGNED_OUT':
          setSession(null);
          toast.info('Sesión cerrada');
          break;
          
        case 'TOKEN_REFRESHED':
          console.log('Token refreshed successfully');
          setSession(currentSession);
          break;
          
        case 'USER_UPDATED':
          setSession(currentSession);
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.state]);

  // Clear guest data from localStorage when logging in
  useEffect(() => {
    if (session) {
      localStorage.removeItem('guestSignupEmail');
      localStorage.removeItem('guestSignupName');
    }
  }, [session]);

  if (loading || checkingGuest) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Always go to chat after login, unless there's a specific return path
  if (session) {
    const returnPath = (location.state as any)?.from || '/chat';
    return <Navigate to={returnPath} replace />;
  }

  return (
    <div className="min-h-screen bg-secondary">
      <AuthHeader />
      <div className="container mx-auto">
        <AuthForm guestEmail={guestData.email} guestName={guestData.name} />
      </div>
    </div>
  );
};

export default LoginPage;
